import React from 'react';
import { Link } from 'react-router-dom';
import { Matter } from '../../types/legislation';

interface LegislationCardProps {
  matter: Matter;
}

const LegislationCard: React.FC<LegislationCardProps> = ({ matter }) => {
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('enacted') || statusLower.includes('adopted')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (statusLower.includes('introduced')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (statusLower.includes('committee')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (statusLower.includes('filed') || statusLower.includes('withdrawn')) {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Link
      to={`/legislation/${matter.MatterId}`}
      className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-nyc-blue font-semibold">{matter.MatterFile}</span>
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
            matter.MatterStatusName
          )}`}
        >
          {matter.MatterStatusName}
        </span>
      </div>

      <h3 className="text-gray-900 font-medium mb-2 line-clamp-2">
        {matter.MatterTitle || matter.MatterName}
      </h3>

      {(matter.MatterBodyName || matter.prime_sponsor_name) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {matter.MatterBodyName && (
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">
              {matter.MatterBodyName}
            </span>
          )}
          {matter.prime_sponsor_name && (
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-blue-50 text-blue-700">
              {matter.prime_sponsor_name}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
        <span>{matter.MatterTypeName}</span>
        <span>Intro: {formatDate(matter.MatterIntroDate)}</span>
        {matter.CouncilVoteDate && <span>Council Vote: {formatDate(matter.CouncilVoteDate)}</span>}
        {matter.matched_attachment_text === 1 && (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">
            Matched in documents
          </span>
        )}
      </div>
    </Link>
  );
};

export default LegislationCard;
